import api from './api';

// Scheduling API Service for Phase 2: Calendar Permissions & Locked Time Periods
export const schedulingService = {
  
  // Calendar Permissions
  getCalendarPermissions: (asGranter = false, asGrantee = false) => {
    const params = { as_granter: asGranter, as_grantee: asGrantee };
    return api.get('/scheduling/calendar-permissions', { params });
  },
  
  createCalendarPermission: (permissionData) => {
    return api.post('/scheduling/calendar-permissions', {
      granter_user_id: permissionData.granter_user_id,
      grantee_user_id: permissionData.grantee_user_id,
      permission_level: permissionData.permission_level,
      expires_at: permissionData.expires_at,
      notes: permissionData.notes
    });
  },
  
  revokeCalendarPermission: (permissionId) => {
    return api.delete(`/scheduling/calendar-permissions/${permissionId}`);
  },
  
  // Locked Time Periods
  getLockedTimePeriods: (userId = null, startDate = null, endDate = null) => {
    const params = {};
    if (userId) params.user_id = userId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return api.get('/scheduling/locked-time-periods', { params });
  },
  
  createLockedTimePeriod: (periodData) => {
    return api.post('/scheduling/locked-time-periods', {
      start_time: periodData.start_time,
      end_time: periodData.end_time,
      reason: periodData.reason,
      description: periodData.description,
      is_recurring: periodData.is_recurring,
      recurrence_pattern: periodData.recurrence_pattern
    });
  },
  
  updateLockedTimePeriod: (periodId, updateData) => {
    return api.put(`/scheduling/locked-time-periods/${periodId}`, {
      start_time: updateData.start_time,
      end_time: updateData.end_time,
      reason: updateData.reason,
      description: updateData.description,
      is_recurring: updateData.is_recurring,
      recurrence_pattern: updateData.recurrence_pattern
    });
  },
  
  deleteLockedTimePeriod: (periodId) => {
    return api.delete(`/scheduling/locked-time-periods/${periodId}`);
  },
  
  // Conflict Detection
  checkSchedulingConflicts: (conflictData) => {
    return api.post('/scheduling/check-conflicts', {
      user_id: conflictData.user_id,
      start_time: conflictData.start_time,
      end_time: conflictData.end_time,
      exclude_event_id: conflictData.exclude_event_id
    });
  },
  
  // Task Dependencies (for future use)
  getTaskDependencies: (taskId) => {
    return api.get(`/scheduling/task-dependencies?task_id=${taskId}`);
  },
  
  createTaskDependency: (dependencyData) => {
    return api.post('/scheduling/task-dependencies', {
      predecessor_task_id: dependencyData.predecessor_task_id,
      successor_task_id: dependencyData.successor_task_id,
      dependency_type: dependencyData.dependency_type,
      lag_days: dependencyData.lag_days,
      is_required: dependencyData.is_required
    });
  },
  
  deleteTaskDependency: (dependencyId) => {
    return api.delete(`/scheduling/task-dependencies/${dependencyId}`);
  },

  // Intelligent Scheduling - Phase 3
  /**
   * Find available time slots for an event
   * @param {Object} request - Scheduling request
   * @param {string} request.title - Event title
   * @param {number} request.duration_minutes - Duration in minutes
   * @param {string} request.event_type - Event type (task, meeting, court_date, etc.)
   * @param {string} request.priority - Priority level (low, medium, high, critical)
   * @param {string} [request.preferred_date] - Preferred date/time (ISO string)
   * @param {string} [request.earliest_date] - Earliest acceptable date (ISO string)
   * @param {string} [request.latest_date] - Latest acceptable date (ISO string)
   * @param {boolean} [request.requires_business_hours] - Must be within business hours
   * @param {string} [request.case_id] - Associated case ID
   * @param {string} [request.project_id] - Associated project ID
   * @param {string} [request.description] - Event description
   * @param {string} [request.location] - Event location
   * @param {number} [max_suggestions] - Maximum number of suggestions to return
   * @returns {Promise<Array>} Array of scheduling suggestions
   */
  findAvailableSlots: async (request, max_suggestions = 5) => {
    try {
      const response = await api.post('/scheduling/find-slots', request, {
        params: { max_suggestions }
      });
      return response.data;
    } catch (error) {
      console.error('Error finding available slots:', error);
      throw error;
    }
  },

  /**
   * Auto-schedule an event with optional automatic creation
   * @param {Object} request - Scheduling request (same format as findAvailableSlots)
   * @param {boolean} [auto_create] - Whether to automatically create the best suggestion
   * @returns {Promise<Object>} Scheduling result with suggestions and optional event_id
   */
  autoSchedule: async (request, auto_create = false) => {
    try {
      const requestWithAutoCreate = { ...request, auto_create };
      const response = await api.post('/scheduling/auto-schedule', requestWithAutoCreate);
      return response.data;
    } catch (error) {
      console.error('Error auto-scheduling event:', error);
      throw error;
    }
  },

  /**
   * Check if a specific time slot is available
   * @param {Object} request - Time slot check request
   * @param {string} request.start_datetime - Start time (ISO string)
   * @param {string} request.end_datetime - End time (ISO string)
   * @param {string} request.event_type - Event type
   * @param {string} [request.exclude_event_id] - Event ID to exclude from conflict check
   * @returns {Promise<Object>} Availability result with conflicts and suggestions
   */
  checkTimeSlotAvailability: async (request) => {
    try {
      const response = await api.post('/scheduling/check-availability', request);
      return response.data;
    } catch (error) {
      console.error('Error checking time slot availability:', error);
      throw error;
    }
  },

  /**
   * Schedule an event at a specific time slot
   * @param {Object} request - Event details (same format as findAvailableSlots)
   * @param {string} start_datetime - Start time (ISO string)
   * @param {string} end_datetime - End time (ISO string)
   * @param {boolean} [force] - Force creation even with warning-level conflicts
   * @returns {Promise<Object>} Created event details
   */
  scheduleWithSpecificSlot: async (request, start_datetime, end_datetime, force = false) => {
    try {
      const response = await api.post('/scheduling/schedule-with-slot', request, {
        params: { start_datetime, end_datetime, force }
      });
      return response.data;
    } catch (error) {
      console.error('Error scheduling with specific slot:', error);
      throw error;
    }
  },

  // ===========================================
  // Deadline Scheduling & Critical Path Analysis
  // ===========================================

  /**
   * Create a new deadline for a case
   */
  createDeadline: async (caseId, deadlineData) => {
    return api.post(`/cases/${caseId}/deadlines`, deadlineData);
  },

  /**
   * Get all deadlines for a case
   */
  getCaseDeadlines: async (caseId, includePast = false) => {
    return api.get(`/cases/${caseId}/deadlines`, {
      params: { include_past: includePast }
    });
  },

  /**
   * Schedule tasks backward from a deadline
   */
  scheduleFromDeadline: async (caseId, deadlineId, options = {}) => {
    return api.post(`/cases/${caseId}/schedule-from-deadline/${deadlineId}`, options);
  },

  /**
   * Get complete timeline for a case
   */
  getCaseTimeline: async (caseId, includeCompleted = false) => {
    return api.get(`/cases/${caseId}/timeline`, {
      params: { include_completed: includeCompleted }
    });
  },

  /**
   * Get critical path analysis for a case
   */
  getCriticalPath: async (caseId, deadlineId = null, useCache = true) => {
    const params = { use_cache: useCache };
    if (deadlineId) params.deadline_id = deadlineId;
    return api.get(`/cases/${caseId}/critical-path`, { params });
  },

  /**
   * Get slack/float time for a task
   */
  getTaskSlack: async (taskId) => {
    return api.get(`/tasks/${taskId}/slack`);
  },

  /**
   * Run what-if scenario analysis
   */
  whatIfAnalysis: async (caseId, request) => {
    return api.post(`/cases/${caseId}/what-if`, request);
  },

  /**
   * Get deadline risk assessment for a case
   */
  getRiskAssessment: async (caseId) => {
    return api.get(`/cases/${caseId}/risk-assessment`);
  },

  /**
   * Get recovery options for schedule delays
   */
  getRecoveryOptions: async (caseId, delayDays) => {
    return api.get(`/cases/${caseId}/recovery-options`, {
      params: { delay_days: delayDays }
    });
  },

  /**
   * Propagate task delay to dependent tasks
   */
  propagateTaskDelay: async (taskId, newDate, autoPropagate = true, createAlerts = true) => {
    return api.post(`/tasks/${taskId}/propagate-delay`, null, {
      params: {
        new_date: newDate,
        auto_propagate: autoPropagate,
        create_alerts: createAlerts
      }
    });
  },

  /**
   * Invalidate critical path cache for a case
   */
  invalidateCriticalPathCache: async (caseId) => {
    return api.post(`/cases/${caseId}/invalidate-cache`);
  }
};

/**
 * Utility functions for working with scheduling data
 */
export const schedulingUtils = {
  /**
   * Format a scheduling suggestion for display
   * @param {Object} suggestion - Scheduling suggestion from API
   * @returns {Object} Formatted suggestion
   */
  formatSuggestion(suggestion) {
    const startDate = new Date(suggestion.start_datetime);
    const endDate = new Date(suggestion.end_datetime);
    
    return {
      ...suggestion,
      formatted_start: startDate.toLocaleString(),
      formatted_end: endDate.toLocaleString(),
      formatted_date: startDate.toLocaleDateString(),
      formatted_time: `${startDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${endDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`,
      duration_hours: (endDate - startDate) / (1000 * 60 * 60),
      confidence_percent: Math.round(suggestion.confidence * 100)
    };
  },

  /**
   * Get human-readable reason for a scheduling suggestion
   * @param {string} reason - Reason code from API
   * @returns {string} Human-readable reason
   */
  getReasonText(reason) {
    const reasonMap = {
      'close_to_preferred_time': 'Close to your preferred time',
      'same_day_as_preferred': 'Same day as preferred',
      'today_available': 'Available today',
      'tomorrow_available': 'Available tomorrow',
      'next_available_slot': 'Next available time slot',
      'user_specified_time': 'Your requested time'
    };
    
    return reasonMap[reason] || 'Available time slot';
  },

  /**
   * Get priority display information
   * @param {string} priority - Priority level
   * @returns {Object} Priority display info
   */
  getPriorityInfo(priority) {
    const priorityMap = {
      'low': { label: 'Low', color: 'gray', icon: '⬇️' },
      'medium': { label: 'Medium', color: 'blue', icon: '➡️' },
      'high': { label: 'High', color: 'orange', icon: '⬆️' },
      'critical': { label: 'Critical', color: 'red', icon: '🚨' }
    };
    
    return priorityMap[priority] || priorityMap['medium'];
  },

  /**
   * Get event type display information
   * @param {string} eventType - Event type
   * @returns {Object} Event type display info
   */
  getEventTypeInfo(eventType) {
    const typeMap = {
      'task': { label: 'Task', icon: '📋', color: 'blue' },
      'meeting': { label: 'Meeting', icon: '👥', color: 'green' },
      'court_date': { label: 'Court Date', icon: '⚖️', color: 'red' },
      'deadline': { label: 'Deadline', icon: '⏰', color: 'orange' },
      'call': { label: 'Call', icon: '📞', color: 'purple' },
      'appointment': { label: 'Appointment', icon: '📅', color: 'indigo' }
    };
    
    return typeMap[eventType] || typeMap['task'];
  },

  /**
   * Validate scheduling request
   * @param {Object} request - Scheduling request
   * @returns {Object} Validation result
   */
  validateRequest(request) {
    const errors = [];
    
    if (!request.title || request.title.trim().length === 0) {
      errors.push('Title is required');
    }
    
    if (!request.event_type) {
      errors.push('Event type is required');
    }
    
    if (!request.duration_minutes || request.duration_minutes < 5) {
      errors.push('Duration must be at least 5 minutes');
    }
    
    if (request.duration_minutes > 480) {
      errors.push('Duration cannot exceed 8 hours');
    }
    
    if (request.preferred_date && request.earliest_date) {
      const preferred = new Date(request.preferred_date);
      const earliest = new Date(request.earliest_date);
      if (preferred < earliest) {
        errors.push('Preferred date cannot be before earliest date');
      }
    }
    
    if (request.earliest_date && request.latest_date) {
      const earliest = new Date(request.earliest_date);
      const latest = new Date(request.latest_date);
      if (earliest > latest) {
        errors.push('Earliest date cannot be after latest date');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
};

export default schedulingService;
